import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Lock, Mic2, RefreshCcw, Repeat2, SlidersHorizontal, Sparkles } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import VersionCard from "@/components/creator/music/VersionCard";
import ProjectActions from "@/components/creator/music/ProjectActions";
import StagePicker from "@/components/creator/music/StagePicker";
import { coverSrc } from "@/lib/creatorApi";
import { GENERATION_PHASES, UPLOAD_COMPOSING, fmtAgo, fmtClock, generateProject, getProject, removeVersion, restoreProject } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";

const POLL_MS = 4000;

function Chip({ children, testId }) {
  return <span data-testid={testId} className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 font-mono text-[11px] uppercase tracking-[0.12em] text-white/75">{children}</span>;
}

function useElapsed(startIso, running) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);
  const start = startIso ? new Date(startIso).getTime() : now;
  return Math.max(0, (now - start) / 1000);
}

function GenerationStatus({ gen }) {
  const running = gen?.status === "running";
  const elapsed = useElapsed(gen?.started_at, running);
  if (!gen) return null;
  if (gen.status === "error") {
    return (
      <div data-testid="generation-error" className="flex items-start gap-3 rounded-2xl border border-rose-400/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>{gen.error || "The song couldn't be created."}</span>
      </div>
    );
  }
  if (!running) return gen.note ? <p data-testid="generation-note" className="rounded-2xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-[13px] text-amber-100">{gen.note}</p> : null;
  return (
    <div data-testid="generation-progress" data-phase={gen.phase} className="rounded-2xl border border-blue-400/30 bg-blue-500/[0.08] px-4 py-4">
      <p className="flex items-center gap-2 text-sm text-blue-100">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span data-testid="generation-phase">{(gen.phase === "composing" && UPLOAD_COMPOSING[gen.start_mode]) || GENERATION_PHASES[gen.phase] || "Working"}…</span>
        <span className="font-mono text-[11px] text-blue-200/70">{fmtClock(elapsed)}</span>
      </p>
      <p className="mt-1.5 text-[12px] text-white/55">
        {gen.phase === "composing" ? `${gen.takes_ready || 0} of 2 versions ready · usually 1–3 minutes.` : "Almost done."} You can leave this page — the song keeps cooking and lands here.
      </p>
    </div>
  );
}

function groupRuns(versions) {
  const runs = [];
  for (const v of [...(versions || [])].reverse()) {
    let g = runs.find((r) => r.run_id === v.run_id);
    if (!g) { g = { run_id: v.run_id, at: v.created_at, items: [] }; runs.push(g); }
    g.items.unshift(v);
  }
  return runs;
}

/** One project — settings, live generation status, every version with its Lyrics Lock check. */
export default function ProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [lyricsOpen, setLyricsOpen] = useState(false);

  const load = useCallback(async () => {
    try { setProject(await getProject(id)); setError(null); }
    catch (e) { setError(e?.response?.status === 404 ? "We couldn't find that project." : safeApiError(e)); }
  }, [id]);

  useEffect(() => { setProject(null); load(); }, [load]);
  const running = project?.generation?.status === "running";
  const checking = (project?.versions || []).some((v) => ["pending", "running"].includes(v.lyrics_check?.status));
  useEffect(() => {
    if (!running && !checking) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [running, checking, load]);

  const runs = useMemo(() => groupRuns(project?.versions), [project?.versions]);

  const regenerate = async () => {
    setRegenerating(true);
    try { setProject(await generateProject(id)); toast.success("Creating new versions", { description: "Your earlier versions stay in the project." }); }
    catch (e) { toast.error("Couldn't start a new version", { description: safeApiError(e) }); }
    finally { setRegenerating(false); }
  };
  const remove = async (v) => {
    if (!window.confirm(`Remove "${v.title}" from this project? The song itself stays in your Songs tab.`)) return;
    try { setProject(await removeVersion(id, v.id)); toast.success("Version removed from the project"); }
    catch (e) { toast.error("Couldn't remove the version", { description: safeApiError(e) }); }
  };
  const restore = async () => {
    try { setProject(await restoreProject(id)); toast.success("Project restored"); }
    catch (e) { toast.error("Couldn't restore", { description: safeApiError(e) }); }
  };

  if (error) {
    return (
      <CreatorShell testId="project-page" back="/create/projects" title="Hmm, that project isn't here" subtitle={error}>
        <Link to="/create/song" className="inline-flex rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-6 py-3 font-display font-semibold">Create Music</Link>
      </CreatorShell>
    );
  }
  if (!project) {
    return <CreatorShell testId="project-page" back="/create/projects"><p className="flex items-center gap-2 text-white/60"><Loader2 className="h-5 w-5 animate-spin" /> Opening your project…</p></CreatorShell>;
  }

  const cover = coverSrc(project);
  return (
    <CreatorShell testId="project-page" back="/create/projects" spine={running ? "generate" : project.versions_count ? "record" : "write"} spineDone={project.versions_count ? ["write", "generate"] : []} wide>
      <div className="grid gap-10 lg:grid-cols-[300px_minmax(0,1fr)]">
        <div className="mx-auto w-full max-w-[260px] lg:max-w-none">
          <div className="aspect-square overflow-hidden rounded-3xl border border-white/[0.08] bg-gradient-to-br from-blue-600/30 via-violet-600/25 to-amber-500/20">
            {cover ? <img src={cover} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center font-display text-6xl font-bold text-white/25">{(project.title || "E")[0]}</div>}
          </div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-white/35">Created {fmtAgo(project.created_at)} · edited {fmtAgo(project.updated_at)}</p>
        </div>
        <div className="min-w-0 space-y-6">
          <div>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-blue-300/80"><Sparkles className="h-3.5 w-3.5" /> Project{project.folder ? ` · ${project.folder}` : ""}</p>
            <h1 data-testid="project-title" className="mt-2 font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">{project.title || "Untitled song"}</h1>
            <p data-testid="project-artist" className="mt-1 text-white/60">{project.artist}</p>
            <div data-testid="project-chips" className="mt-4 flex flex-wrap items-center gap-2">
              {!project.deleted_at ? <StagePicker project={project} onChanged={(p) => (p ? setProject((cur) => ({ ...cur, stage: p.stage })) : null)} /> : null}
              <Chip testId="project-chip-genre">{project.genre_label}</Chip>
              <Chip testId="project-chip-voice">{project.voice_label}</Chip>
              {project.generation?.model_label ? <Chip testId="project-chip-model">{project.generation.model_label}</Chip> : null}
              {project.arrangement_label && project.arrangement_label !== "Solo" ? <Chip testId="project-chip-arrangement">{project.arrangement_label}</Chip> : null}
              {(project.start_mode || "scratch") === "scratch" ? (
                <>
                  <Chip testId="project-chip-bpm">{project.bpm ? `${project.bpm} BPM` : "Auto BPM"}</Chip>
                  <Chip testId="project-chip-key">{project.key || "Auto key"}</Chip>
                  {project.duration_sec ? <Chip testId="project-chip-length">{fmtClock(project.duration_sec)}</Chip> : null}
                </>
              ) : <Chip testId="project-chip-source">{project.start_mode === "grow_loop" ? "Tempo & key from your loop" : "Tempo & key from your upload"}</Chip>}
              {project.lyrics_lock ? <Chip testId="project-chip-lock"><Lock className="mr-1 inline h-3 w-3" />Lyrics Lock</Chip> : null}
            </div>
          </div>
          {project.deleted_at ? (
            <div className="flex items-center justify-between rounded-2xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              This project is in Deleted. <button type="button" data-testid="project-restore" onClick={restore} className="rounded-full bg-white/[0.15] px-3 py-1 font-mono text-[10px] uppercase tracking-wider">Restore</button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" data-testid="project-regenerate" onClick={regenerate} disabled={running || regenerating} className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-5 py-2.5 font-display text-sm font-semibold disabled:opacity-40">
                {regenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />} {project.versions_count ? "Generate new versions" : "Generate"}
              </button>
              <button type="button" data-testid="project-edit" onClick={() => navigate(`/create/song?project=${project.id}`)} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-display text-sm text-white/85 hover:bg-white/[0.06]">
                <SlidersHorizontal className="h-4 w-4" /> Edit lyrics & settings
              </button>
              {project.versions?.some((v) => v.kind === "generated") ? (
                <>
                  <Link to={`/create/project/${project.id}/sing`} data-testid="project-sing" className="inline-flex items-center gap-2 rounded-full border border-rose-300/40 bg-rose-500/10 px-5 py-2.5 font-display text-sm text-rose-100 hover:bg-rose-500/20">
                    <Mic2 className="h-4 w-4" /> Sing it myself
                  </Link>
                  <Link to={`/create/project/${project.id}/replace`} data-testid="project-replace" className="inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-amber-500/10 px-5 py-2.5 font-display text-sm text-amber-100 hover:bg-amber-500/20">
                    <Repeat2 className="h-4 w-4" /> Replace lead vocal
                  </Link>
                </>
              ) : null}
              <ProjectActions project={project} onChanged={(p) => (p ? setProject(p) : null)} onDeleted={() => navigate("/create/projects")} />
            </div>
          )}
          <GenerationStatus gen={project.generation} />
          {runs.length ? runs.map((run, i) => (
            <section key={run.run_id || i} data-testid={`project-run-${i}`} className="space-y-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{i === 0 ? "Latest" : "Earlier"} · {fmtAgo(run.at)}</p>
              <div className="grid gap-3 xl:grid-cols-2">
                {run.items.map((v) => <VersionCard key={v.id} projectId={project.id} version={v} source={project.source_audio} lyrics={project.lyrics} hasLyrics={Boolean((project.lyrics || "").trim())} onChanged={load} onRemove={remove} onRegenerate={regenerate} />)}
              </div>
            </section>
          )) : !running ? <p data-testid="project-no-versions" className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-white/55">No versions yet — press Generate to create the first two.</p> : null}
          {project.lyrics ? (
            <div>
              <button type="button" data-testid="project-lyrics-toggle" onClick={() => setLyricsOpen((o) => !o)} className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/50 hover:text-white">{lyricsOpen ? "Hide lyrics" : "Show lyrics"}</button>
              {lyricsOpen ? <pre data-testid="project-lyrics" className="mt-3 whitespace-pre-wrap rounded-2xl border border-white/[0.08] bg-black/30 p-4 font-body text-sm leading-relaxed text-white/75">{project.lyrics}</pre> : null}
            </div>
          ) : null}
        </div>
      </div>
    </CreatorShell>
  );
}
